import React from 'react';

import useAutoBounceScroll from '../../hooks/useAutoBounceScroll';
import useHorizontalTimelineScroll from '../../hooks/useHorizontalTimelineScroll';
import { formatHebrewWeekday } from '../../utils/date';
import { numbersToTime } from '../../utils/time';

const CATEGORY_DEFINITIONS = [
  { key: 'tfilot', title: 'תפילות' },
  { key: 'shiurim', title: 'שיעורים' },
  { key: 'other', title: 'נוסף' }
];

const STATUS_LABELS = {
  past: 'הסתיים',
  now: 'עכשיו',
  starting: 'עוד מעט',
  planned: 'בהמשך'
};

function categoryKeyForItem(item) {
  return item.type === 'tfila' ? 'tfilot' : item.type === 'shiur' ? 'shiurim' : 'other';
}

function ScheduleItem({ item, isTimeline = false, isTimelineFocus = false }) {
  const isTfila = item.type === 'tfila';
  const hasMeta = Boolean(item.crfd1_location || item.crfd1_subtitle);
  const locationText = item.crfd1_location ? item.crfd1_location.replace('שיעור ', '') : '';
  const status = Object.prototype.hasOwnProperty.call(STATUS_LABELS, item.status) ? item.status : 'planned';
  const time = numbersToTime(item.hours, item.minutes);
  const typeClass = isTfila
    ? 'schedule-card--tfila'
    : item.type === 'shiur'
      ? 'schedule-card--shiur'
      : 'schedule-card--other';

  return (
    <article
      className={`detail panel-item panel-item--schedule schedule-card ${typeClass} schedule-card--${status} ${isTimeline ? 'schedule-card--timeline' : ''} ${hasMeta ? 'schedule-card--with-meta' : ''}`}
      data-event-status={status}
      aria-label={`${item.crfd1_title}, ${time}, ${STATUS_LABELS[status]}`}
      data-timeline-focus={isTimelineFocus ? 'true' : undefined}
    >
      <div className="schedule-card-main">
        <div className="schedule-title" title={item.crfd1_title}>
          {item.crfd1_title}
        </div>
        <div className={`schedule-time ${isTfila ? 'bold' : ''}`}>
          {time}
        </div>
        <div className="schedule-meta" aria-hidden={hasMeta ? undefined : true}>
          {item.crfd1_subtitle && (
            <span className="schedule-meta-lecturer" title={item.crfd1_subtitle}>{item.crfd1_subtitle}</span>
          )}
          {locationText && <span className="schedule-meta-location" title={locationText}>{locationText}</span>}
        </div>
      </div>
    </article>
  );
}

function getScheduleItemKey(day, item, index) {
  return `${day.dateKey}-${item.type}-${item.crfd1_occurrenceid || item.crfd1_sourceid || index}-${item.crfd1_title}`;
}

function findTimelineFocusKey(items) {
  const currentItem = items.find((entry) => entry.item.status === 'now');
  if (currentItem) {
    return currentItem.key;
  }

  const startingItem = items.find((entry) => entry.item.status === 'starting');
  if (startingItem) {
    return startingItem.key;
  }

  const nextItem = items.find((entry) => entry.item.status !== 'past');
  return nextItem?.key || items[items.length - 1]?.key || '';
}

function buildScheduleModel(days = []) {
  const normalizedDays = [];
  let scheduledDayCount = 0;

  days.forEach((day) => {
    const normalizedDay = {
      ...day,
      schedule: Array.isArray(day?.schedule) ? day.schedule : []
    };
    normalizedDays.push(normalizedDay);
    if (normalizedDay.schedule.length > 0) {
      scheduledDayCount += 1;
    }
  });

  if (!scheduledDayCount) {
    return { sections: [], rows: [], timeline: [], timelineFocusKey: '', signature: '', itemCount: 0 };
  }

  const sectionsByKey = new Map(
    CATEGORY_DEFINITIONS.map((category) => [category.key, { ...category, days: [] }])
  );
  const rows = [];
  const timeline = [];
  const signatureParts = [];
  let itemCount = 0;

  normalizedDays.forEach((day, dayNumber) => {
    if (!day.schedule.length) {
      return;
    }

    const title = `יום ${formatHebrewWeekday(day.weekday)}`;
    const shouldShowLegacyDayTitle = scheduledDayCount > 1 || dayNumber > 0;
    const categoryItems = { tfilot: [], shiurim: [], other: [] };

    if (shouldShowLegacyDayTitle) {
      rows.push({
        type: 'day-title',
        key: `title-${day.dateKey}`,
        title
      });
    }

    day.schedule.forEach((item, index) => {
      const categoryKey = categoryKeyForItem(item);
      const entry = {
        type: 'schedule-item',
        key: getScheduleItemKey(day, item, index),
        item,
        dayTitle: title,
        dateKey: day.dateKey
      };

      rows.push(entry);
      timeline.push(entry);
      categoryItems[categoryKey].push(entry);
      signatureParts.push(
        `${entry.key}-${item.status}-${item.crfd1_subtitle || ''}-${item.crfd1_location || ''}`
      );
      itemCount += 1;
    });

    CATEGORY_DEFINITIONS.forEach((category) => {
      if (categoryItems[category.key].length) {
        sectionsByKey.get(category.key).days.push({
          key: day.dateKey,
          title,
          items: categoryItems[category.key]
        });
      }
    });
    signatureParts.push(`${day.dateKey}-${title}`);
  });

  return {
    sections: CATEGORY_DEFINITIONS.flatMap((category) => {
      const section = sectionsByKey.get(category.key);
      return section.days.length ? [section] : [];
    }),
    rows,
    timeline: [...timeline].reverse(),
    timelineFocusKey: findTimelineFocusKey(timeline),
    signature: signatureParts.join('|'),
    itemCount
  };
}

function SectionedSchedule({ sections }) {
  return sections.map((section) => (
    <section key={section.key} className={`schedule-primary-section schedule-primary-section--${section.key}`}>
      <h1 className="schedule-section-title">{section.title}</h1>
      <div className="schedule-section-days">
        {section.days.map((day) => (
          <section
            key={`${section.key}-${day.key}`}
            className="schedule-section-day"
            style={{ '--schedule-day-item-count': day.items.length }}
          >
            <h2 className="segment-title-inline schedule-section-day-title">{day.title}</h2>
            <div className="schedule-section-day-list">
              {day.items.map((entry) => (
                <ScheduleItem key={entry.key} item={entry.item} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  ));
}

function ScheduleRows({ rows }) {
  return rows.map((row) =>
    row.type === 'day-title' ? (
      <h2 key={row.key} className="segment-title-inline">
        {row.title}
      </h2>
    ) : (
      <ScheduleItem key={row.key} item={row.item} />
    )
  );
}

function TimelineSchedule({ model, containerRef, hasOverflow }) {
  const dayGroups = [];
  model.timeline.forEach((entry) => {
    let group = dayGroups[dayGroups.length - 1];
    if (!group || group.dateKey !== entry.dateKey) {
      group = { dateKey: entry.dateKey, title: entry.dayTitle, entries: [] };
      dayGroups.push(group);
    }
    group.entries.push(entry);
  });

  return (
    <>
      <div className="schedule-timeline-desktop">
        <div
          ref={containerRef}
          className={`schedule-timeline-track${hasOverflow ? ' schedule-timeline-track--overflow' : ''}`}
          dir="ltr"
          data-timeline-flow="rtl"
          tabIndex={0}
          role="region"
          aria-label="תפילות ושיעורים"
        >
          {dayGroups.map((group) => (
            <section key={group.dateKey} className="schedule-timeline-day" aria-label={group.title}>
              <h2 className="schedule-timeline-day-title"><span>{group.title}</span></h2>
              <div className="schedule-timeline-day-cards">
                {group.entries.map((entry) => (
                  <ScheduleItem
                    key={entry.key}
                    item={entry.item}
                    isTimeline
                    isTimelineFocus={entry.key === model.timelineFocusKey}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
      <div className="container panel-content schedule-timeline-mobile-list">
        <ScheduleRows rows={model.rows} />
      </div>
    </>
  );
}

function SchedulePanel({ days, loading, groupByType = false, variant }) {
  const layout = variant || (groupByType ? 'sections' : 'list');
  const scheduleModel = React.useMemo(() => buildScheduleModel(days), [days]);
  const {
    containerRef: verticalContainerRef,
    resetScroll: resetVerticalScroll,
    hasOverflow: hasVerticalOverflow
  } = useAutoBounceScroll({
    enabled: layout !== 'timeline' && scheduleModel.itemCount > 0,
    pageStepRatio: 0.92,
    durationDownMs: 2000,
    durationUpMs: 1000,
    pauseBottomMs: 8000,
    pauseTopMs: 8000
  });
  const horizontalScroll = useHorizontalTimelineScroll({
    enabled: layout === 'timeline' && scheduleModel.itemCount > 0,
    signature: `${scheduleModel.signature}-${scheduleModel.timelineFocusKey}`
  });

  React.useEffect(() => {
    if (layout !== 'timeline') {
      resetVerticalScroll();
    }
  }, [layout, resetVerticalScroll, scheduleModel.signature]);

  const rootClassName = [
    'glass container panel-fit schedule-panel',
    `schedule-panel--${layout}`
  ].join(' ');

  return (
    <div className={rootClassName} style={{ flexGrow: 1 }}>
      {layout !== 'sections' && (
        <h1 className={layout === 'overview' && scheduleModel.itemCount ? 'schedule-overview-mobile-title' : undefined}>
          תפילות ושיעורים
        </h1>
      )}
      {scheduleModel.itemCount ? (
        layout === 'timeline' ? (
          <TimelineSchedule
            model={scheduleModel}
            containerRef={horizontalScroll.containerRef}
            hasOverflow={horizontalScroll.hasOverflow}
          />
        ) : (
          <div
            ref={verticalContainerRef}
            className={`container panel-content ${hasVerticalOverflow ? 'panel-content-scroll' : ''}`}
            style={{ justifyContent: 'flex-start' }}
            tabIndex={layout === 'overview' ? 0 : undefined}
            role={layout === 'overview' ? 'region' : undefined}
            aria-label={layout === 'overview' ? 'תפילות ושיעורים' : undefined}
          >
            {layout === 'overview' ? (
              <>
                <div className="schedule-overview-desktop">
                  <SectionedSchedule sections={scheduleModel.sections} />
                </div>
                <div className="container schedule-overview-mobile-list">
                  <ScheduleRows rows={scheduleModel.rows} />
                </div>
              </>
            ) : layout === 'sections' ? (
              <SectionedSchedule sections={scheduleModel.sections} />
            ) : (
              <ScheduleRows rows={scheduleModel.rows} />
            )}
          </div>
        )
      ) : (
        <div className="container panel-content panel-placeholder">
          {loading ? 'טוען מידע...' : 'אין מידע להצגה'}
        </div>
      )}
    </div>
  );
}

export default React.memo(SchedulePanel);
